/**
 * 회원 관리 관련 React Query 훅
 *
 * 회원 목록 조회는 현재 `pages/AdminMembersListPage`가 Firestore를 직접 읽고 있으므로
 * 본 모듈에서는 생성(mutation)만 노출한다. 추후 목록을 React Query로 마이그레이션할 때
 * `useMembers` query 훅을 함께 추가하고 onSuccess에서 invalidate하도록 보강한다.
 */

import { useMutation } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@/config/firebase";

// --- 로컬 타입 정의 (백엔드 Cloud Function 요청/응답) ---
// 백엔드 contract: packages/@vlab-corporation/firebase-functions/src/member/create-member.ts

interface CreateMemberRequest {
  centerName: string;
  name: string;
  mobile: string;
}

interface CreateMemberResponse {
  success: true;
  /** 서버에서 발급된 memberId (8자리 이상, 10_000_000+) */
  memberId: number;
}

/** 회원 생성 시 외부에서 전달하는 파라미터 */
export interface CreateMemberParams {
  centerName: string;
  name: string;
  mobile: string;
}

// 백엔드 contract: member/update-member.ts, member/delete-member.ts

interface UpdateMemberRequest {
  centerId: number;
  memberId: number;
  name?: string;
  mobile?: string;
}

interface DeleteMemberRequest {
  centerId: number;
  memberId: number;
}

interface MemberMutationResponse {
  success: true;
}

/** 회원 수정 시 외부에서 전달하는 파라미터 (name/mobile 중 1개 이상) */
export interface UpdateMemberParams {
  centerId: number;
  memberId: number;
  name?: string;
  mobile?: string;
}

/** 회원 삭제(소프트) 시 외부에서 전달하는 파라미터 */
export interface DeleteMemberParams {
  centerId: number;
  memberId: number;
}

/**
 * 회원 생성 mutation
 * - 서버에서 memberId가 발급되어 응답으로 반환된다.
 * - 호출자가 후속 처리에 ID가 필요하면 mutation.data.memberId로 옵트인.
 * - 목록은 페이지가 onSuccess 콜백에서 직접 refetch하므로 여기서는 invalidate하지 않는다.
 */
export function useCreateMember() {
  return useMutation({
    mutationFn: async (params: CreateMemberParams) => {
      const fn = httpsCallable<CreateMemberRequest, CreateMemberResponse>(
        getFirebaseFunctions(),
        "createMember",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

/**
 * 회원 수정 mutation
 * - 백엔드가 top-level/ERP-nested 양쪽 doc을 단일 transaction으로 갱신한다.
 * - 목록·상세는 호출자가 onSuccess에서 직접 refetch한다.
 */
export function useUpdateMember() {
  return useMutation({
    mutationFn: async (params: UpdateMemberParams) => {
      const fn = httpsCallable<UpdateMemberRequest, MemberMutationResponse>(
        getFirebaseFunctions(),
        "updateMember",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

/**
 * 회원 삭제(소프트) mutation
 * - top-level isActive:false + ERP-nested removedAt. 물리 삭제하지 않는다.
 */
export function useDeleteMember() {
  return useMutation({
    mutationFn: async (params: DeleteMemberParams) => {
      const fn = httpsCallable<DeleteMemberRequest, MemberMutationResponse>(
        getFirebaseFunctions(),
        "deleteMember",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}
